import { useState, useEffect, useRef } from 'react';
import useWebSocket from 'react-use-websocket';
import { getMemoryIndex } from '../services/BeholderService';

const DEFAULT_BWS_URL = 'wss://stream.binance.com:9443/ws';
const BOOK_TICKER_REST = 'https://api.binance.com/api/v3/ticker/bookTicker';

function binanceWsBase() {
    return String(process.env.REACT_APP_BWS_URL || DEFAULT_BWS_URL).replace(/\/+$/, '');
}

function extractBook(payload) {
    if (!payload || typeof payload !== 'object') return null;
    if (payload.result === undefined && payload.id && !payload.b && !payload.a && !payload.data)
        return null;

    const ticker = payload.data && (payload.data.b || payload.data.a || payload.data.bidPrice)
        ? payload.data
        : payload;

    const bid = ticker.b ?? ticker.bidPrice ?? ticker.bestBid ?? ticker.bid;
    const ask = ticker.a ?? ticker.askPrice ?? ticker.bestAsk ?? ticker.ask;
    if (bid == null && ask == null) return null;

    const next = {
        bid: bid != null ? String(bid) : '0',
        ask: ask != null ? String(ask) : '0'
    };

    if (parseFloat(next.bid) <= 0 && parseFloat(next.ask) <= 0) return null;
    return next;
}

/**
 * props:
 * - symbol
 * - onChange
 */
function SymbolPrice(props) {
    const symbol = (props.symbol || '').toUpperCase();
    const [book, setBook] = useState({ bid: '0', ask: '0' });
    const onChangeRef = useRef(props.onChange);
    onChangeRef.current = props.onChange;

    function applyBook(next) {
        if (!next) return;
        setBook(next);
        if (onChangeRef.current) onChangeRef.current(next);
    }

    useEffect(() => {
        if (!symbol) return undefined;
        let cancelled = false;

        async function seedPrice() {
            try {
                const response = await fetch(`${BOOK_TICKER_REST}?symbol=${symbol}`);
                const data = await response.json();
                if (!cancelled && data?.bidPrice && data?.askPrice)
                    applyBook({ bid: data.bidPrice, ask: data.askPrice });
            } catch (_) {
                try {
                    const memory = await getMemoryIndex(symbol, 'BOOK');
                    const current = memory?.current || memory;
                    const bid = current?.bestBid ?? current?.bid;
                    const ask = current?.bestAsk ?? current?.ask;
                    if (!cancelled && (parseFloat(bid) > 0 || parseFloat(ask) > 0))
                        applyBook({
                            bid: String(bid ?? ask),
                            ask: String(ask ?? bid)
                        });
                } catch (err) {
                    console.error(err);
                }
            }
        }

        setBook({ bid: '0', ask: '0' });
        seedPrice();
        return () => { cancelled = true; };
    }, [symbol]);

    const socketUrl = symbol ? `${binanceWsBase()}/${symbol.toLowerCase()}@bookTicker` : null;
    const { lastJsonMessage } = useWebSocket(socketUrl, {
        shouldReconnect: () => true,
        reconnectInterval: 3000
    }, Boolean(symbol));

    useEffect(() => {
        applyBook(extractBook(lastJsonMessage));
    }, [lastJsonMessage]);

    return (
        <div className="form-group">
            <label htmlFor="side">Market Price:</label><br />
            BID: {book.bid}<br />
            ASK: {book.ask}
        </div>
    );
}

export default SymbolPrice;
